import * as angular from "angular"
import { escape } from "./regexp"

export const equals = angular.equals

export function isArray(arr: any): arr is any[] {
    return Array.isArray(arr) || arr instanceof Array
}

export function isDate(value: any): value is Date {
    return Object.prototype.toString.call(value) === '[object Date]'
}

export function isDefined(value: any): boolean {
    return typeof value !== 'undefined';
}

export function isFunction(value: any): value is Function {
    return typeof value === 'function';
}

export function isNumber(value: any): value is number {
    return typeof value === 'number';
}

export function isObject(value: any): boolean {
    // http://jsperf.com/isobject4
    return value !== null && typeof value === 'object';
}

export function isString(value: any): value is string {
    return typeof value === 'string';
}

export function isUndefined(value: any): boolean {
    return typeof value === 'undefined';
}

export function isUndefinedOrNull(value: any): boolean {
    return isUndefined(value) || value == null;
}

export function stringOfEnum(enumClass, value): string {
    for (const k in enumClass) {
        if (enumClass[k] == value) {
            return k;
        }
    }
    return null;
}


export function checkEnumValue(enumClass, value): boolean {
    return !!stringOfEnum(enumClass, value)
}

export function endsWith(content: string, suffix: string): boolean {
    return content.indexOf(suffix, content.length - suffix.length) !== -1;
}

export function addListener(elt: Node, event: string, handler: (e: Event) => void) {

    const h = e => handler(e)
    elt.addEventListener(event, h)

    return () => {
        elt.removeEventListener(event, h)
    }

}

export function sortDates(a: Date, b: Date) {
    return (isDate(a) ? a.valueOf() : 0) - (isDate(b) ? b.valueOf() : 0)
}

export function padLeft(value: any, length: number, char: string): string {
    let text = value.toString();
    if (text.length < length) {
        let pad = Array(1 + length - text.length).join(char);
        text = pad + text;
    }
    return text;
}

export function pad2(value: any) {
    return padLeft(value, 2, "0");
}

export function containsText(a: string, b: string) {
    return a && b && (new RegExp(escape(b), "i")).test(b)
}

export function getStackTrace() {

    let caller = undefined

    try {
        throw new Error()
    } catch (error) {
        caller = error?.stack?.split(/\s+at\s+/)?.slice(2)
    }

    return caller

}

export function isImageContentType(contentType: string) {

    switch (contentType) {

        case "image/jpeg":
        case "image/png":
        case "image/gif":
            return true

        default:
            return false

    }

}

/**
 * Returns the complete CSS class of an icon
 *
 * - If "icon" includes spaces, it is returned as is
 * - If it starts with "icon-", then returned prefixed with "icon "
 * - Otherwise, returned prefixed with "icon icon-"
 *
 * @param icon Icon
 * @returns Complete CSS class for the icon
 */
export function getIconClass(icon: string) {
    return (!icon)
        ? null
        : icon.includes(" ")
            ? icon
            : icon.startsWith("icon-")
                ? `icon ${icon}`
                : `icon icon-${icon}`
}

export function getHasFeature<Keys extends string>(set: boolean | Record<Keys, boolean>) {

    const hasFeature = (set === true)
        ? () => true
        : (set === false)
            ? () => false
            : (key: Keys) => set[key]

    return hasFeature

}

const caseInsensitiveComparer = new Intl.Collator(undefined, { sensitivity: 'base' });

export function caseInsensitiveCompare(a: string, b: string) {
    return caseInsensitiveComparer.compare(a, b)
}

/**
 * Rounds a floating-point number to a given number of decimal places,
 * compensating for floating-point drift by adding an EPSILON scaled to the 
 * magnitude of the value being rounded. Note: it uses the 'AwayFromZero' rule for midpoint, which has impact on rounding negative numbers.
 * @param n The raw number to round.
 * @param decimals The number of decimal places.
 * @returns A number rounded to 'decimals' palaces.
 */
export function safeRound(n: number, decimals: number): number {
    const factor = 10 ** decimals;
    const scaled = Math.abs(n * factor);
    // scale EPSILON so that the 'nudge' is large enough even when 'scaled' is a big number
    const epsilonScaled = Number.EPSILON * scaled;
    return Math.sign(n) * (Math.round(scaled + epsilonScaled) / factor);
}